import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { GlossaryBlueprint } from "@/types-games";
import { BlueprintData, FormSection } from "../types";
import { useBlueprintState } from "../useBlueprintState";
// Basic Info Component removed - now in structure-menu sidebar
// import BlueprintBasicInfo from "./BlueprintBasicInfo";
import BlueprintSectionsManager from "./BlueprintSectionsManager";
import { UnifiedWizardHeader } from "../../shared/UnifiedWizardHeader";
import CodePreview from "./CodePreview";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { generateTempId } from "@/lib/utils/uuid";
import MultiSelectDropdown, {
  MultiSelectOption,
} from "@/components/ui/dropdowns/MultiSelectDropdownWithButton";
import AddSectionButton from "../AddSectionButton";
import { generateBlueprintCodePreviewWithGame, downloadBlueprintCode } from "@/lib/integrations/blueprintCodePreview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/DefaultSelectDropdown";
import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { UnsavedChangesDialog } from "@/components/ui/UnsavedChangesDialog";
import { getAvailableViewModesForGame } from "@/lib/utils/platformMapping";
import { templatesStore } from "@/lib/stores/templatesStore";
import { useItemLock } from "@/hooks/useItemLock";
import { LockStatusBanner } from "@/components/ui/LockStatusBanner";
import { useToast } from "@/hooks/use-toast";
import { useWizardDraft, TemplateDraftData } from "@/hooks/useWizardDraft";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useLensesOptional } from '@/contexts/LensesContext';
import { getCompanyRoles } from '@/lib/utils/permissions';
import { useDevRoleSync } from '@/hooks/useDevRoleSync';

/**
 * Generate a unique default name for a new template based on existing templates
 * @param gameId - The game ID to check for existing templates
 * @returns A unique name like "Untitled_Template_001"
 */
function generateDefaultTemplateName(gameId?: string): string {
  const prefix = "Untitled_Template_";
  const allTemplates = templatesStore.getData();
  
  // Filter templates for the current game and find existing "Untitled_Template_XXX" names
  const existingNumbers: number[] = [];
  const regex = new RegExp(`^${prefix}(\\d{3})$`);
  
  allTemplates
    .filter(template => !gameId || template.game_id === gameId)
    .forEach(template => {
      const match = template.name?.match(regex);
      if (match) {
        existingNumbers.push(parseInt(match[1], 10));
      }
    });
  
  // Find the next available number
  let nextNumber = 1;
  while (existingNumbers.includes(nextNumber)) {
    nextNumber++;
  }
  
  // Format with leading zeros (001, 002, etc.)
  return `${prefix}${String(nextNumber).padStart(3, '0')}`;
}

type WizardMode = "create" | "edit" | "view";
type ViewDisplayMode = "structured" | "documentation" | "json";

interface EditBlueprintWizardCompactProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: GlossaryBlueprint;
  inline?: boolean; // If true, renders inline without modal overlay
  mode?: WizardMode; // Initial mode - defaults to "view" if initialData exists, "create" otherwise
  onEdit?: () => void; // Called when edit button is clicked in view mode
  onDelete?: () => void; // Called when delete button is clicked in view mode
  onSave?: (blueprint: {
    id?: string;
    game_id?: string;
    name: string;
    description: string;
    tags: string[];
    types?: string[]; // Make types optional for compatibility
    type: string;
    data: BlueprintData;
    settings: Record<string, unknown>; // Allow any settings, not just color
    version?: 
      | { changes: Record<string, unknown>; version: number; }
      | { major: number; minor: number; patch: number; };
    folder?: string | null;
    owner?: string | null;
    edit_permissions?: 'Open' | 'Locked';
  }) => void;
  onUnsavedChangesChange?: (hasChanges: boolean, saveHandler: () => void, discardHandler?: () => void) => void; // Callback for unsaved changes state
}

export default function EditBlueprintWizardCompact({
  isOpen,
  onClose,
  initialData,
  inline = false,
  mode: initialMode,
  onEdit,
  onDelete,
  onSave,
  onUnsavedChangesChange,
}: EditBlueprintWizardCompactProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { tags, isLoading: tagsLoading } = useTagsDataContext();
  const { types, isLoading: typesLoading } = useTypesDataContext();
  const { selectedGame, currentUser } = useSimplifiedUserData();
  const lensesContext = useLensesOptional();
  
  // Sync dev role override with preview mode (only in dev environments)
  useDevRoleSync();

  // Check if user can edit based on owner and permissions (without lock consideration)
  const canEditByPermission = useMemo(() => {
    if (!initialData) return true; // Can always edit new items
    // If no owner, anyone can edit
    if (!initialData.owner) return true;
    // If user is owner, can edit
    if (initialData.owner === currentUser?.id) return true;
    // If locked and user is not owner, cannot edit
    if (initialData.edit_permissions === 'Locked') return false;
    // If open, anyone can edit
    return true;
  }, [initialData, currentUser?.id]);

  // Determine the mode: use prop if provided, otherwise infer from initialData
  const [wizardMode, setWizardMode] = useState<WizardMode>(
    initialMode || (initialData ? "view" : "create")
  );

  // Item lock system for collaborative editing
  const {
    lockStatus,
    canEdit: canEditByLock,
    isAcquiring: isAcquiringLock,
    lockError,
    isLockedByOther,
    refreshLockStatus,
    forceLock,
  } = useItemLock({
    itemId: initialData?.id,
    itemType: 'template',
    gameId: selectedGame?.id,
    autoAcquire: true,
    isEditMode: wizardMode === 'edit',
    onLockLost: () => {
      toast({
        title: 'Edit lock lost',
        description: 'Another user has taken over editing. Your changes may not be saved.',
        variant: 'destructive',
      });
      // Switch to view mode when lock is lost
      setWizardMode('view');
    },
  });

  // Combined edit permission: must have both permission AND lock
  const canEdit = canEditByPermission && canEditByLock;

  // View display mode state (structured, unity, godot)
  const [viewDisplayMode, setViewDisplayMode] = useState<ViewDisplayMode>("structured");

  // Unsaved changes dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<'close' | 'cancel' | null>(null);
  
  // Saving state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dev View state

  // Draft persistence for tab switching
  // Always load drafts for existing items to restore edit mode state
  // Saving is handled conditionally based on current wizardMode
  const {
    draftData,
    hasDraftData,
    saveDraftData,
    clearDraft,
    saveImmediately,
  } = useWizardDraft<TemplateDraftData>({
    draftType: 'template',
    gameId: selectedGame?.id,
    itemId: initialData?.id,
    // Always enabled to load drafts - we need to check if user was in edit mode
    enabled: true,
  });
  
  // Track if we've already restored the wizard mode from draft
  const hasRestoredModeRef = useRef(false);
  
  // Reset the ref when item changes (switching to a different template)
  useEffect(() => {
    hasRestoredModeRef.current = false;
  }, [initialData?.id]);
  
  // Restore wizard mode from draft on initial load
  // This ensures edit mode is preserved when switching tabs
  useEffect(() => {
    if (!hasRestoredModeRef.current && hasDraftData && draftData?.wizardMode) {
      // Only restore if we have permission to edit and the draft was in edit mode
      if (draftData.wizardMode === 'edit' && canEditByPermission) {
        setWizardMode('edit');
      }
      hasRestoredModeRef.current = true;
    }
  }, [hasDraftData, draftData, canEditByPermission]);

  // Use the custom hook for state management
  const blueprintState = useBlueprintState({ initialData, draftData });

  // NOTE: We no longer sync from templatesStore to blueprintState because:
  // 1. Role visibility changes now go through the LensesContext functions
  // 2. Both left side and right sidebar use the same blueprintState functions
  // 3. Syncing from store was causing changes to be overwritten/reset
  // The blueprintState is now the single source of truth during editing

  // Register blueprint state with Lenses context
  // Register once when component mounts and update when sections change
  useEffect(() => {
    if (lensesContext) {
      lensesContext.registerBlueprintState(
        blueprintState.sections,
        blueprintState.toggleSectionForRole,
        blueprintState.toggleFieldForRole,
        blueprintState.setSectionVisibilityState,
        blueprintState.setFieldVisibilityState,
        blueprintState.resetToInitialData
      );
    }
  }, [lensesContext, blueprintState.sections, blueprintState.toggleSectionForRole, blueprintState.toggleFieldForRole, blueprintState.setSectionVisibilityState, blueprintState.setFieldVisibilityState]);
  
  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (lensesContext) {
        lensesContext.unregisterBlueprintState();
      }
    };
  }, [lensesContext]);

  // Initialize default name for new templates
  useEffect(() => {
    if (!initialData && wizardMode === "create" && !blueprintState.blueprintName) {
      const defaultName = generateDefaultTemplateName(selectedGame?.id);
      blueprintState.setBlueprintName(defaultName);
    }
  }, [initialData, wizardMode, selectedGame?.id]); // Don't include blueprintState to avoid loops

  // Track unsaved changes
  // Transform initialData to match the format of getCurrentData()
  const initialDataForComparison = useMemo(() => {
    if (!initialData) return {};
    return {
      name: initialData.name,
      description: initialData.description,
      tags: initialData.tags || [],
      types: initialData.types || [],
      color: initialData.settings?.color || '',
      sections: initialData.data?.sections || [],
      renderOrder: initialData.data?.renderOrder || [],
      owner: initialData.owner,
      edit_permissions: initialData.edit_permissions,
    };
  }, [initialData]);

  const unsavedChanges = useUnsavedChanges(
    initialDataForComparison,
    () => ({
      name: blueprintState.blueprintName,
      description: blueprintState.blueprintDescription,
      tags: blueprintState.itemTags,
      types: blueprintState.itemTypes,
      color: blueprintState.blueprintColor,
      sections: blueprintState.sections,
      renderOrder: blueprintState.renderOrder,
      owner: blueprintState.owner,
      edit_permissions: blueprintState.editPermissions,
    }),
    wizardMode === "edit" || wizardMode === "create"
  );

  // Check for changes whenever blueprint state changes
  useEffect(() => {
    if (wizardMode === "edit" || wizardMode === "create") {
      unsavedChanges.checkForChanges();
    }
  }, [
    blueprintState.blueprintName,
    blueprintState.blueprintDescription,
    blueprintState.itemTags,
    blueprintState.itemTypes,
    blueprintState.blueprintColor,
    blueprintState.sections,
    blueprintState.renderOrder,
    blueprintState.owner,
    blueprintState.editPermissions,
    wizardMode,
    unsavedChanges,
  ]);

  // Save draft data when form state changes (for persistence across tab switches)
  useEffect(() => {
    if ((wizardMode === "edit" || wizardMode === "create") && selectedGame?.id) {
      // Get current draft data from state and include wizardMode
      const currentDraftData = {
        ...blueprintState.getDraftData(),
        wizardMode, // Include wizard mode so it's restored when switching back to this tab
      };
      // Defer the save to avoid state updates during render
      queueMicrotask(() => {
        saveDraftData(currentDraftData);
      });
    }
  }, [
    blueprintState.blueprintName,
    blueprintState.blueprintDescription,
    blueprintState.itemTags,
    blueprintState.itemTypes,
    blueprintState.blueprintColor,
    blueprintState.sections,
    blueprintState.renderOrder,
    blueprintState.owner,
    blueprintState.editPermissions,
    blueprintState.blueprintStatus,
    wizardMode,
    selectedGame?.id,
    saveDraftData,
    blueprintState,
  ]);

  // Store save handler in ref for stable callback
  const saveHandlerRef = useRef<() => void>();
  
  // Notify parent of unsaved changes state (for tab system integration)
  useEffect(() => {
    if (onUnsavedChangesChange) {
      if (wizardMode === "edit" || wizardMode === "create") {
        // Create stable save handler that calls the latest handleSave
        const stableSaveHandler = () => {
          if (saveHandlerRef.current) {
            saveHandlerRef.current();
          }
        };
        // Create discard handler that clears the draft
        const discardHandler = () => {
          clearDraft();
        };
        onUnsavedChangesChange(unsavedChanges.hasChanges, stableSaveHandler, discardHandler);
      } else {
        // When switching to view mode, clear the unsaved changes flag
        // This ensures the tab doesn't show unsaved changes after saving
        onUnsavedChangesChange(false, () => {}, () => {});
      }
    }
  }, [unsavedChanges.hasChanges, wizardMode, onUnsavedChangesChange, clearDraft]);

  // Convert tags and types to MultiSelectOption format
  const tagOptions: MultiSelectOption[] = tags.map((tag) => ({
    id: tag.id,
    label: tag.name,
  }));

  const typeOptions: MultiSelectOption[] = types.map((type) => ({
    id: type.id,
    label: type.name,
  }));
  
  // Get available view modes for this game
  const availableViewModes = getAvailableViewModesForGame(selectedGame);

  // Generate code preview based on view display mode
  const codePreview = useMemo(() => {
    if (wizardMode !== "view" || viewDisplayMode === "structured" || viewDisplayMode === "documentation") {
      return null;
    }

    if (!initialData?.id || !selectedGame?.id) {
      return null;
    }

    try {
      if (viewDisplayMode === "json") {
        // Generate JSON preview of the blueprint structure
        return JSON.stringify(initialData, null, 2);
      }
      
      // Use the new export template system for code generation
      return generateBlueprintCodePreviewWithGame(
        initialData,
        selectedGame,
        viewDisplayMode as any
      );
    } catch (error) {
      console.error('Error generating code preview:', error);
      return `// Error generating preview: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }, [wizardMode, viewDisplayMode, initialData, selectedGame]);

  // Helper function to check if only documentation changed
  const isDocumentationOnlyChange = useCallback(() => {
    if (!initialData) return false;

    // Normalize arrays (treat undefined and [] as equal)
    const normalizeArray = (arr: any) => {
      if (!arr || (Array.isArray(arr) && arr.length === 0)) return [];
      return arr;
    };

    // Compare everything except documentation
    const hasStructuralChanges = 
      blueprintState.blueprintName !== initialData.name ||
      blueprintState.blueprintDescription !== initialData.description ||
      JSON.stringify(normalizeArray(blueprintState.itemTags)) !== JSON.stringify(normalizeArray(initialData.tags)) ||
      JSON.stringify(normalizeArray(blueprintState.itemTypes)) !== JSON.stringify(normalizeArray(initialData.types)) ||
      blueprintState.blueprintColor !== initialData.settings?.color;


    // Compare sections structure (excluding documentation)
    const currentData = blueprintState.getBlueprintData();
    const initialDataStructure = initialData.data as any;

    // Remove documentation fields for comparison
    const stripDocumentation = (data: any): any => {
      if (!data) return data;
      if (typeof data !== 'object') return data;
      
      // Handle arrays
      if (Array.isArray(data)) {
        return data.map(item => stripDocumentation(item));
      }
      
      // Handle objects
      const result: any = {};
      for (const [key, value] of Object.entries(data)) {
        // Skip documentation fields
        if (key === '_sectionDocumentation' || key === 'documentation') continue;
        
        if (value && typeof value === 'object') {
          result[key] = stripDocumentation(value);
        } else {
          result[key] = value;
        }
      }
      return result;
    };

    const currentStructure = stripDocumentation(currentData);
    const initialStructure = stripDocumentation(initialDataStructure);

    // Sort keys before stringifying for consistent comparison
    const sortKeys = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) return obj.map(sortKeys);
      return Object.keys(obj).sort().reduce((result: any, key: string) => {
        result[key] = sortKeys(obj[key]);
        return result;
      }, {});
    };

    const sortedCurrent = sortKeys(currentStructure);
    const sortedInitial = sortKeys(initialStructure);

    const areEqual = JSON.stringify(sortedCurrent) === JSON.stringify(sortedInitial);
    
    // Debug logging
    if (!areEqual) {
      console.log('📊 Structure comparison details:');
      console.log('Current structure:', JSON.stringify(sortedCurrent, null, 2));
      console.log('Initial structure:', JSON.stringify(sortedInitial, null, 2));
    }
    
    return areEqual;
  }, [initialData, blueprintState]);

  // Handle save
  const handleSave = async () => {
    // Update ref whenever this function is called
    saveHandlerRef.current = handleSave;
    if (!blueprintState.blueprintName.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const blueprintData = blueprintState.getBlueprintData();

      // Check if only documentation changed
      const onlyDocChanged = isDocumentationOnlyChange();
      
      // Debug logging
      if (onlyDocChanged) {
        console.log('📝 Only documentation changed - preserving version:', initialData?.version);
      } else {
        console.log('🔄 Structural changes detected - version will increment');
      }

      const blueprint = {
        id: initialData?.id || generateTempId(),
        game_id: selectedGame?.id,
        name: blueprintState.blueprintName,
        description: blueprintState.blueprintDescription,
        tags: blueprintState.itemTags,
        types: blueprintState.itemTypes,
        type: "glossary",
        data: blueprintData,
        settings: {
          ...initialData?.settings, // Preserve existing settings (like library object filters)
          color: blueprintState.blueprintColor as string, // Override color
        },
        folder: initialData?.folder || null, // Add folder field
        owner: blueprintState.owner,
        edit_permissions: blueprintState.editPermissions,
        status: blueprintState.blueprintStatus,
        // If only documentation changed, preserve existing version
        // Otherwise, don't send version - API will calculate next version
        ...(onlyDocChanged && initialData?.version ? { version: initialData.version } : {}),
      };
      
      await onSave?.(blueprint);
      
      // Mark as saved and clear draft
      unsavedChanges.markAsSaved();
      clearDraft();
      
      // If we're in inline mode, switch to view mode after save
      // If modal mode, close the modal
      if (inline) {
        setWizardMode("view");
        // Keep on structured view after saving
        setViewDisplayMode("structured");
      } else {
        // Modal mode - close after save
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle revert - reload initial data without closing
  const handleRevert = () => {
    if (!initialData) return;
    
    console.log('[EditBlueprintWizard] Reverting to saved version');
    
    // Reload all blueprint data from initialData
    blueprintState.setBlueprintName(initialData.name);
    blueprintState.setBlueprintDescription(initialData.description || '');
    blueprintState.setBlueprintColor(initialData.settings?.color || '#6e35ff');
    blueprintState.setItemTags(initialData.tags || []);
    blueprintState.setItemTypes(initialData.types || []);
    blueprintState.setBlueprintStatus(initialData.status || 'draft');
    blueprintState.setOwner(initialData.owner || null);
    blueprintState.setEditPermissions(initialData.edit_permissions || 'Open');
    
    // Reload sections from initialData
    if (initialData.data?.sections) {
      blueprintState.setSections(initialData.data.sections as FormSection[]);
    }
    
    // Reset unsaved changes
    unsavedChanges.reset();
    
    toast({
      title: 'Changes Reverted',
      description: 'All changes have been discarded and the template has been restored to its saved state.',
    });
  };

  // Handle edit button click
  const handleEdit = () => {
    if (!canEdit) {
      return; // Don't allow editing if user doesn't have permission
    }
    // Switch back to structured view first
    setViewDisplayMode("structured");
    setWizardMode("edit");
    onEdit?.();
  };

  // Handle close/cancel
  // In inline mode, delegates to the tab system for unsaved changes handling
  const handleClose = () => {
    // Check for unsaved changes first
    const hasChanges = unsavedChanges.checkForChanges();
    console.log('[BlueprintWizard] Has unsaved changes:', hasChanges);
    
    // In inline mode with create/edit, let the tab system handle unsaved changes dialog
    // This prevents showing two dialogs (one from wizard, one from tab system)
    if (inline && (wizardMode === "create" || wizardMode === "edit")) {
      console.log('[BlueprintWizard] Inline mode - delegating to tab system');
      
      // Ensure the tab system knows about unsaved changes before calling onClose
      // (the useEffect might not have run yet due to async state updates)
      if (onUnsavedChangesChange) {
        const stableSaveHandler = () => {
          if (saveHandlerRef.current) {
            saveHandlerRef.current();
          }
        };
        const discardHandler = () => {
          clearDraft();
        };
        onUnsavedChangesChange(hasChanges, stableSaveHandler, discardHandler);
      }
      
      onClose();
      return;
    }
    
    // Modal mode - show wizard's own unsaved changes dialog
    if (hasChanges && (wizardMode === "edit" || wizardMode === "create")) {
      setPendingAction('close');
      setShowUnsavedDialog(true);
      return;
    }

    // No unsaved changes, proceed with close
    proceedWithClose();
  };

  const proceedWithClose = () => {
    // Modal mode or view mode - close
    blueprintState.resetForm();
    unsavedChanges.reset();
    clearDraft(); // Clear draft when discarding changes
    onClose();
  };

  // Handle backdrop click (modal only)
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle preview toggle
  const handlePreviewToggle = () => {
    blueprintState.setIsPreviewMode(!blueprintState.isPreviewMode);
  };

  const handleCodeDownload = (engine: string) => {
    const gameName = selectedGame?.name || 'Game';
    const templateName = initialData?.name || 'Template';
    
    if (engine === "json") {
      // Download JSON structure with GameName-T-TemplateName format
      const sanitizeFilename = (name: string) => name.replace(/[^a-zA-Z0-9-_]/g, '_').replace(/_{2,}/g, '_');
      const jsonContent = JSON.stringify(initialData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFilename(gameName)}-T-${sanitizeFilename(templateName)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // Use the new export template system for code download
      downloadBlueprintCode(
        initialData,
        selectedGame,
        engine as any,
        gameName,
        templateName
      );
    }
  };

  // In modal mode, check isOpen. In inline mode, always render
  if (!inline && !isOpen) return null;

  const wizardContentInner = (
    <div className={inline ? "relative w-full h-full flex flex-col" : "relative bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col"}>
      {/* Lock Status Banner */}
      {isLockedByOther && (
        <div className={inline ? "px-6 pt-4 max-w-4xl mx-auto w-full" : "px-6 pt-4"}>
          <LockStatusBanner
            lockStatus={lockStatus}
            isAcquiring={isAcquiringLock}
            lockError={lockError}
            canEdit={canEdit}
            onRefresh={refreshLockStatus}
            onForceTakeover={async () => {
              const success = await forceLock();
              if (success) {
                toast({
                  title: 'Edit access acquired',
                  description: 'You now have edit access to this template.',
                });
                setWizardMode('edit');
              }
            }}
            canForceTakeover={canEditByPermission}
            size="md"
          />
        </div>
      )}

      {/* Header */}
      <div className={inline ? "flex-shrink-0 pt-6 border-b border-grey-G2 bg-background" : "flex-shrink-0 pt-6 px-6 border-b border-grey-G2"}>
          <div className={inline ? "max-w-4xl mx-auto px-6 pb-6" : ""}>
          <UnifiedWizardHeader
            title={blueprintState.blueprintName || "Untitled Template"}
            mode={wizardMode}
            itemType="blueprint"
            itemId={initialData?.id}
            onTitleChange={blueprintState.setBlueprintName}
            onCancel={handleClose}
            onRevert={initialData?.id ? handleRevert : undefined}
            onSubmit={handleSave}
            onEdit={handleEdit}
            onDelete={onDelete}
            isSubmitting={isSubmitting}
            submitDisabled={!blueprintState.blueprintName.trim()}
            submitText="Create Template"
            hasUnsavedChanges={unsavedChanges.hasChanges}
            tags={blueprintState.itemTags}
            tagOptions={tagOptions}
            version={initialData?.version}
            owner={blueprintState.owner}
            editPermissions={blueprintState.editPermissions}
            canEdit={canEdit}
            status={blueprintState.blueprintStatus}
            onStatusChange={blueprintState.setBlueprintStatus}
          />
        </div>
      </div>

      {/* Content */}
      <div className={inline ? "flex-1 overflow-y-auto bg-background" : "flex-1 overflow-y-auto p-6"}>
        {/* View Mode Dropdown */}
        {wizardMode === "view" && (
          <div className={inline ? "max-w-4xl mx-auto px-6 pt-4 pb-2" : "pb-2"}>
            <Select value={viewDisplayMode} onValueChange={(value) => setViewDisplayMode(value as ViewDisplayMode)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('platform.wizards.blueprints.view_mode')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="structured">{t('platform.wizards.blueprints.normal_view')}</SelectItem>
                <SelectItem value="documentation">{t('platform.wizards.blueprints.documentation_view')}</SelectItem>
                <SelectItem value="json">{t('platform.wizards.blueprints.json_structure')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Role Preview Banner */}
        {lensesContext?.previewMode && lensesContext?.selectedRole && selectedGame?.company_id && (() => {
          const currentRole = getCompanyRoles(selectedGame.company_id).find(r => r.id === lensesContext.selectedRole);
          const roleColor = currentRole?.data?.color || '#3b82f6';
          return (
            <div className={inline ? "max-w-4xl mx-auto px-6 py-2" : "py-2"}>
              <div 
                className="rounded-md px-4 py-3 flex items-center gap-2"
                style={{
                  backgroundColor: `${roleColor}15`,
                  borderLeft: `4px solid ${roleColor}`,
                  color: roleColor
                }}
              >
                <IconProvider iconstring="fa-eye" size={16} style="auto" className="text-sidebar-foreground/70" />
                <span className="text-sm font-medium flex items-center gap-2 flex-wrap">
                  <strong>Preview Mode:</strong> 
                  <span>Viewing as</span>
                  {currentRole?.data?.color && (
                    <div 
                      className="w-2.5 h-2.5 rounded-full border flex-shrink-0" 
                      style={{ 
                        backgroundColor: roleColor,
                        borderColor: roleColor
                      }}
                    />
                  )}
                  <strong>
                    {currentRole?.role_name || 'Unknown Role'}
                  </strong>
                </span>
              </div>
            </div>
          );
        })()}

        {codePreview ? (
          // Code Preview Mode (Unity/Godot/C++/TypeScript/Roblox)
          <div className={inline ? "max-w-4xl mx-auto px-6 pb-6" : ""}>
            <CodePreview
              code={codePreview}
              language={

                viewDisplayMode === "json" ? "json" :
                "csharp"
              }
              gameName={selectedGame?.name}
              templateName={initialData?.name}
            />
          </div>
        ) : (
          // Structured View Mode
          <div className={inline ? `max-w-4xl mx-auto px-6 space-y-4 ${wizardMode === "view" ? "py-2" : "py-4"}` : "space-y-4"}>
            {/* Can be edited independently without entering edit mode */}

            {/* Sections */}
            <BlueprintSectionsManager
              sections={blueprintState.sections}
              renderOrder={blueprintState.renderOrder}
              onSectionsChange={blueprintState.setSections}
              onRenderOrderChange={blueprintState.setRenderOrder}
              onDeleteSection={blueprintState.deleteSection}
              onUpdateSection={blueprintState.updateSection}
              isEditingExisting={!!initialData}
              lastAddedSectionId={blueprintState.lastAddedSectionId}
              lastAddedFieldId={blueprintState.lastAddedFieldId}
              onClearLastAddedFieldId={() => blueprintState.setLastAddedFieldId(undefined)}
              disabled={wizardMode === "view"}
              showDocumentation={viewDisplayMode === "documentation"}
              onSectionVisibilityChange={blueprintState.setSectionVisibilityState}
              onFieldVisibilityChange={blueprintState.setFieldVisibilityState}
            />

            {/* Developer Notes */}
            {/* <BlueprintDeveloperNotes
                developerNotes={blueprintState.developerNotes}
                renderOrder={blueprintState.renderOrder}
                onDeveloperNotesChange={blueprintState.setDeveloperNotes}
                onRenderOrderChange={blueprintState.setRenderOrder}
                onAddNote={blueprintState.addDeveloperNote}
                onDeleteNote={blueprintState.deleteDeveloperNote}
                onUpdateNote={blueprintState.updateDeveloperNote}
              /> */}
          </div>
        )}
      </div>

      {/* Add Section Button - only show when not in view mode and in structured view */}
      {wizardMode !== "view" && !codePreview && (
        <div className={inline ? "flex items-center justify-center border-t border-black bg-background" : "flex items-center justify-center mx-6 mb-6 border-t border-black"}>
          <div className={inline ? "max-w-4xl w-full" : ""}>
            <AddSectionButton onAddSection={blueprintState.addSection} />
          </div>
        </div>
      )}
    </div>
  );

  // Return modal or inline version
  if (inline) {
    return (
      <>
        {wizardContentInner}
        {/* Unsaved Changes Dialog */}
        <UnsavedChangesDialog
          isOpen={showUnsavedDialog}
          onClose={() => {
            setShowUnsavedDialog(false);
            setPendingAction(null);
          }}
          onSave={async () => {
            handleSave();
            setShowUnsavedDialog(false);
            setPendingAction(null);
          }}
          onDiscard={() => {
            unsavedChanges.reset();
            proceedWithClose();
            setShowUnsavedDialog(false);
            setPendingAction(null);
          }}
          title={t('platform.wizards.blueprints.unsaved_template_changes')}
          description="You have unsaved changes to this template. Would you like to save them before closing?"
        />
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex justify-center items-center">
        {/* Blurred black overlay */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleBackdropClick}
        />
        <div className="relative z-10 w-full max-w-7xl h-[90vh]">
          {wizardContentInner}
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={() => {
          setShowUnsavedDialog(false);
          setPendingAction(null);
        }}
        onSave={async () => {
          handleSave();
          setShowUnsavedDialog(false);
          setPendingAction(null);
        }}
        onDiscard={() => {
          unsavedChanges.reset();
          proceedWithClose();
          setShowUnsavedDialog(false);
          setPendingAction(null);
        }}
        title={t('platform.wizards.blueprints.unsaved_template_changes')}
        description="You have unsaved changes to this template. Would you like to save them before closing?"
      />
    </>
  );
}
